import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { appTheme } from "shared/theme";

const COLORS = {
  primary: appTheme.palette.green.main,
  secondary: appTheme.palette.gray.main,
  bg: appTheme.palette.white.main,
  softBg: appTheme.palette.gray.light,
  border: "rgba(0,0,0,0.08)",
};

const textSx = {
  fontFamily: appTheme.typography?.body1?.fontFamily,
  color: COLORS.secondary,
  lineHeight: 1.75,
};

const subheadSx = {
  ...textSx,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: COLORS.primary,
  fontWeight: 700,
  mb: 1.25,
  fontSize: { xs: 12, sm: 13 },
};

const Title = ({ children }) => (
  <Typography
    variant="h4"
    component="h3"
    sx={{
      fontFamily:
        appTheme.typography?.h1?.fontFamily ||
        appTheme.typography?.body1?.fontFamily,
      textTransform: "uppercase",
      color: COLORS.secondary,
      fontWeight: 800,
      lineHeight: 1.15,
      fontSize: { xs: 22, sm: 26, md: 30 },
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const ImageFrame = ({ src, alt, compact }) => (
  <Box
    sx={{
      width: "100%",
      borderRadius: { xs: 3, md: 4 },
      overflow: "hidden",
      border: `1px solid ${COLORS.border}`,
      backgroundColor: appTheme.palette.white?.main,
      boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        display: "block",
        width: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: compact
          ? { xs: 280, sm: 320, md: 360 }
          : { xs: 360, sm: 440, md: 520 },
      }}
    />
  </Box>
);

const StoreBadges = ({ center }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      mt: 3,
      flexWrap: "wrap",
      justifyContent: center ? "center" : "flex-start",
      alignItems: "center",
    }}
  >
    <Box
      component="a"
      href="https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Download on the App Store"
        src="/appleBadge.jpg"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>

    <Box
      component="a"
      href="https://play.google.com/store/apps/details?id=com.sevenday.mobile&pcampaignid=web_share7DAY"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Get it on Google Play"
        src="/googleBadge.png"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>
  </Box>
);

const FeatureSection = ({ section, index }) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const reversed = mdUp ? Boolean(section.reverseOnDesktop) : false;

  const isCompact =
    section.image?.compact ||
    section.title.toLowerCase().includes("notifications") ||
    section.title.toLowerCase().includes("reporting") ||
    section.title.toLowerCase().includes("face") ||
    section.title.toLowerCase().includes("vehicle");

  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 4, md: 6 },
        border: `1px solid ${COLORS.border}`,
        background:
          index % 2 === 0
            ? "linear-gradient(180deg, rgba(92,181,179,0.08) 0%, rgba(255,255,255,1) 55%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,1) 55%)",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, sm: 4, md: 5 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        alignItems="center"
        direction={reversed ? "row-reverse" : "row"}
      >
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "center",
                md: reversed ? "flex-start" : "flex-end",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: {
                  xs: 520,
                  sm: 640,
                  md: isCompact ? 560 : 680,
                },
              }}
            >
              <ImageFrame
                src={section.image.src}
                alt={section.image.alt}
                compact={isCompact}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              maxWidth: 680,
              mx: { xs: "auto", md: 0 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography sx={subheadSx}>{section.kicker}</Typography>

            <Title>{section.title}</Title>

            <Box sx={{ mt: 2 }}>
              {section.paragraphs.map((p, i) => (
                <Typography
                  key={i}
                  sx={{
                    ...textSx,
                    mb: i === section.paragraphs.length - 1 ? 0 : 1.75,
                    fontSize: { xs: 14.5, sm: 15.5, md: 16 },
                  }}
                >
                  {p}
                </Typography>
              ))}
            </Box>

            {section.hasStoreBadges && <StoreBadges center={!mdUp} />}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const ProductContentGuests = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const headerVariant = useMemo(
    () => (isMobile ? "h3" : isTablet ? "h2" : "h1"),
    [isMobile, isTablet],
  );

  const sections = [
    {
      title: "Book Online",
      kicker:
        "Explore and book premium spaces and experiences—anytime, anywhere.",
      paragraphs: [
        "With the 7DAY app, you can browse and secure everything from boats and vehicles to homes and unique venues, all in one place.",
        "Prefer a quicker way? Let our AI Reservation Assistant do the work—just tell it what you need, and it will find the best options for you.",
        "Available on web and mobile, our seamless booking system makes planning effortless—whether you’re doing it yourself or letting AI handle it.",
      ],
      image: { src: "/imgs/classic/guests/calendar.jpg", alt: "Calendar" },
      hasStoreBadges: true,
    },
    {
      title: "Manage your bookings",
      kicker:
        "Stay in control of your team’s bookings—past, present, and future.",
      paragraphs: [
        "With our built-in calendar, you can easily view and manage all trip details, update upcoming bookings, and export or share plans with your team.",
        "7DAY also integrates with Outlook, Google Calendar, and Microsoft 365, so everything stays in sync—no double handling required.",
      ],
      image: {
        src: "/imgs/classic/guests/reservations.png",
        alt: "Manage bookings calendar",
      },
      reverseOnDesktop: true,
    },

    {
      title: "RSVP Requests & Attendance Tracking",
      kicker:
        "Invite attendees, collect responses, and keep everyone in the loop.",
      paragraphs: [
        "At the time of making a reservation request, guests can choose to send RSVP requests to potential attendees and include a custom message.",
        "Invitees can accept or decline, and the organiser is notified as responses come in. If an invitee accepts, the reservation will appear in their login.",
        "Guests can use a built-in 7DAY RSVP template or write their own message, view all RSVP responses directly on the reservation, remove invitees if needed, and send reminders manually or automatically to anyone who has not yet responded.",
      ],
      image: {
        src: "/imgs/classic/guests/RSVP.png",
        alt: "RSVP requests and attendance tracking",
      },
      reverseOnDesktop: false,
    },
    {
      title: "Plan your entire trip",
      kicker: "Keep all your travel plans in one place.",
      paragraphs: [
        "Build a complete itinerary around your reservation by adding flights, transport, activities, restaurant bookings, meetings, and events. Keep everyone aligned with a single timeline that brings together everything happening before, during, and after your stay.",
        "Whether you're planning a team retreat, client event, or group getaway, the itinerary feature makes it easy to organise travel logistics and experiences in one central place. Share plans with attendees, stay on schedule, and ensure nothing gets missed.",
      ],
      image: {
        src: "/imgs/classic/guests/itinerary.png",
        alt: "Guest itinerary planning",
      },
      reverseOnDesktop: true,
    },
    {
      title: "Real-time notifications",
      kicker: "Stay informed without lifting a finger.",
      paragraphs: [
        "Our in-app notification center keeps you updated on all booking activity as it happens—from approvals and declines to cancellations.",
        "With real-time alerts, you’ll spend less time chasing updates and more time focusing on what matters.",
      ],
      image: {
        src: "/imgs/classic/guests/notifications.jpg",
        alt: "Notifications",
      },
       reverseOnDesktop: false,
      imageCompact: true,
    },
    {
      title: "User Management",
      kicker: "Easily manage who can access and use 7DAY across your team.",
      paragraphs: [
        "Invite staff securely, assign roles, and control access levels with ease. Whether you’re adding new team members or adjusting permissions, our user management tools put you in control.",
        "With four security profile types, you decide who can view, book, or manage staff bookings—keeping everything organised and secure.",
      ],
      image: { src: "/imgs/classic/guests/users.png", alt: "User management" },
      reverseOnDesktop: true,
    },
    {
      title: "Reporting",
      kicker: "Take the guesswork out of work perks.",
      paragraphs: [
        "Understand what your employees value most with access to customisable reports that reveal booking trends, preferences, and usage.",
        "From monthly summaries to financial year breakdowns, our reporting tools help you tailor future perks and make smarter decisions—backed by real data.",
      ],
      image: {
        src: "/imgs/classic/guests/report.jpg",
        alt: "Reporting",
        compact: true,
      },
       reverseOnDesktop: false,
    },
    {
      title: "In-App Messaging",
      kicker: "Connect directly with hosts—before or after you book.",
      paragraphs: [
        "Our built-in messenger makes it easy to ask questions, confirm details, or coordinate plans with the host. Whether you're enquiring about a venue or discussing an upcoming reservation, everything stays in one place—secure, simple, and streamlined.",
        "No more chasing emails or phone calls—just clear, real-time communication right inside the 7DAY app.",
      ],
      image: { src: "/imgs/classic/guests/chat.jpg", alt: "In-app messaging" },
      reverseOnDesktop: true,
    },
    {
      title: "Admin Centre",
      kicker: "All the admin power — none of the admin pain!",
      paragraphs: [
        "Run everything from one place. The 7DAY Admin Centre gives you full control over your account—manage users and access, billing, and more.",
        "It’s your command centre for keeping the whole operation organised, secure, and running smoothly.",
      ],
      image: { src: "/imgs/classic/guests/admin.jpg", alt: "Admin Centre" },
      reverseOnDesktop: false,
    },
    {
      title: "Face & Vehicle Recognition Access (Keyless Check-In)",
      kicker:
        "Smoother arrivals, fewer headaches, and a premium first impression every time.",
      paragraphs: [
        "Hosts have the option to make check-ins effortless with keyless entry powered by face and vehicle recognition. You arrive, access is granted instantly, and your experience stays secure—no key handovers, no lockboxes, no “where’s the code?” texts.",
      ],
      image: {
        src: "/imgs/classic/guests/facerec.png",
        alt: "Face and vehicle recognition",
        compact: true,
      },
      reverseOnDesktop: true,
    },
   
    {
      title: "Add-On Perks & Extras",
      kicker: "Upgrade any booking in one click!",
      paragraphs: [
        "Turn a booking into a full experience with optional add-ons at checkout. Guests can request extras like catering, drinks packages, alcohol, or even massage services (depending on the space)—all confirmed upfront and organised before they arrive.",
        "More convenience for guests, more revenue for hosts, and a smoother day for everyone.",
      ],
      image: {
        src: "/imgs/classic/guests/massage.png",
        alt: "Add-ons",
        compact: true,
      },
      reverseOnDesktop: false,
    },
  ];

  return (
    <Box sx={{ backgroundColor: COLORS.bg }}>
      <Box
        sx={{
          background:
            "radial-gradient(900px 500px at 15% 0%, rgba(92,181,179,0.16) 0%, rgba(255,255,255,0) 60%), radial-gradient(800px 420px at 85% 0%, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0) 55%)",
        }}
      >
        <Container
          id="product"
          maxWidth="lg"
          sx={{
            pt: { xs: 14, md: 20 },
            pb: { xs: 6, md: 10 },
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography
              component={headerVariant}
              variant={headerVariant}
              sx={{
                color: COLORS.secondary,
                fontFamily:
                  appTheme.typography?.h1?.fontFamily ||
                  appTheme.typography?.body1?.fontFamily,
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: "0.06em",
                lineHeight: 1.05,
                fontSize: { xs: 34, sm: 44, md: 56 },
              }}
            >
              Guest Features
            </Typography>

            <Typography
              variant="h6"
              component="h2"
              sx={{
                ...textSx,
                mt: 1.25,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: COLORS.primary,
                fontWeight: 800,
                fontSize: { xs: 12, sm: 13, md: 14 },
              }}
            >
              The only app you need for corporate work perks
            </Typography>

            <Box
              sx={{
                mt: 3,
                height: 1,
                width: { xs: "100%", sm: 640 },
                mx: "auto",
                backgroundColor: COLORS.border,
              }}
            />
          </Box>

          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, md: 4 },
            }}
          >
            {sections.map((section, index) => (
              <FeatureSection
                key={section.title}
                section={section}
                index={index}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          backgroundColor: COLORS.softBg,
          borderTop: `1px solid ${COLORS.border}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: "center", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                sx={{
                  fontFamily:
                    appTheme.typography?.h1?.fontFamily ||
                    appTheme.typography?.body1?.fontFamily,
                  textTransform: "uppercase",
                  fontWeight: 900,
                  color: COLORS.secondary,
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  lineHeight: 1.2,
                }}
              >
                Ready to book perks that people actually use?
              </Typography>
              <Typography sx={{ ...textSx, mt: 1, maxWidth: 680 }}>
                Browse, book, invite your team, and manage everything from one
                place.
              </Typography>
            </Box>

            <StoreBadges center />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
